import { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, InputAdornment, List, ListItemButton, Paper, Stack, TextField, Typography } from '@mui/material';
import { Search } from 'lucide-react';
import { useAgentRuns } from '@diggens/shared-hooks';
import type { AgentRunSummary, AgentSlug } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { agentStyles } from '../../theme/theme';
import { agentLabel, formatTime, formatWhen, orderRuns, runOutcome, type RunOutcome } from '@diggens/chat';

// Outlined, since the theme greys out every filled chip
const OUTCOME_CHIPS: Record<RunOutcome, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  needs_approval: { label: 'Needs approval', color: 'warning' },
  running: { label: 'Running', color: 'primary' },
  stalled: { label: 'Stalled', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  incomplete: { label: 'Incomplete', color: 'warning' },
  rejected: { label: 'Rejected', color: 'error' },
  expired: { label: 'Expired', color: 'default' },
  done: { label: 'Done', color: 'success' },
};

export function RunOutcomeChip({ run }: { run: Pick<AgentRunSummary, 'status' | 'stalled' | 'plan'> }) {
  const chip = OUTCOME_CHIPS[runOutcome(run)];
  return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" sx={{ flexShrink: 0 }} />;
}

type RunFilter = 'all' | 'needs_approval' | 'running' | 'failed' | 'decided';

const outcomeIn = (outcomes: RunOutcome[]) => (run: AgentRunSummary) => outcomes.includes(runOutcome(run));

const FILTERS: Array<{ id: RunFilter; label: string; test: ((run: AgentRunSummary) => boolean) | null }> = [
  { id: 'all', label: 'All', test: null },
  { id: 'needs_approval', label: 'Needs approval', test: outcomeIn(['needs_approval']) },
  { id: 'running', label: 'Running', test: outcomeIn(['needs_approval', 'running', 'stalled']) },
  { id: 'failed', label: 'Failed', test: outcomeIn(['failed', 'stalled', 'incomplete']) },
  // The approval history: plans an admin approved or rejected, and those that expired undecided
  { id: 'decided', label: 'Decided', test: (run) => run.plan?.decision != null || runOutcome(run) === 'expired' },
];

/** Whether a run is in the filter and mentions every word of the search, in its request, plan, agent or person. */
function matches(run: AgentRunSummary, filter: (typeof FILTERS)[number], words: string[]): boolean {
  if (filter.test && !filter.test(run)) return false;
  const haystack = [run.title, run.plan?.goal, run.userName, agentLabel(run.agent)].join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

interface RunListProps {
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
  /** Shows only the runs this agent worked in, its delegated steps included. */
  agent: AgentSlug | null;
  onClearAgent: () => void;
}

/** The tenant's runs of the last 7 days: waiting for approval first, then running, then newest first; filterable and searchable. */
export function RunList({ selectedRunId, onSelect, agent, onClearAgent }: RunListProps) {
  const { data, isLoading, error, dataUpdatedAt } = useAgentRuns();
  const [filterId, setFilterId] = useState<RunFilter>('all');
  const [search, setSearch] = useState('');

  const runs = data ? orderRuns(data.filter((run) => !agent || run.agents.includes(agent))) : [];
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const filter = FILTERS.find((candidate) => candidate.id === filterId) ?? FILTERS[0]!;
  const shown = runs.filter((run) => matches(run, filter, words));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Runs
      </Typography>
      {agent && <Chip label={`Agent: ${agentLabel(agent)}`} size="small" color="primary" variant="outlined" onDelete={onClearAgent} sx={{ mb: 1 }} />}
      <TextField
        size="small"
        fullWidth
        placeholder="Search requests, plans and people"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, my: 1.5 }}>
        {FILTERS.map((candidate) => {
          const count = candidate.test ? runs.filter(candidate.test).length : runs.length;
          return (
            <Chip
              key={candidate.id}
              label={count > 0 ? `${candidate.label} ${count}` : candidate.label}
              size="small"
              onClick={() => setFilterId(candidate.id)}
              color={filterId === candidate.id ? 'primary' : 'default'}
              variant={filterId === candidate.id ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && <Alert severity="error">{error instanceof Error ? error.message : 'Could not load agent runs'}</Alert>}
      {data && runs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {agent ? `No runs with ${agentLabel(agent)} in the last 7 days.` : 'No agent runs in the last 7 days.'}
        </Typography>
      )}
      {runs.length > 0 && shown.length === 0 && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No runs match.
          </Typography>
          <Button
            size="small"
            onClick={() => {
              setFilterId('all');
              setSearch('');
            }}
          >
            Clear search and filters
          </Button>
        </Box>
      )}

      {shown.length > 0 && (
        <List disablePadding sx={{ maxHeight: { md: 'calc(100vh - 340px)' }, overflowY: 'auto' }}>
          {shown.map((run) => (
            <ListItemButton key={run.runId} selected={run.runId === selectedRunId} onClick={() => onSelect(run.runId)} sx={{ borderRadius: 1, alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  mt: 1,
                  flexShrink: 0,
                  borderRadius: '50%',
                  backgroundColor: run.status === 'running' && !run.stalled ? agentStyles.statusColors.processing : 'transparent',
                  ...(run.status === 'running' && !run.stalled ? agentStyles.agentPulse : {}),
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {run.title}
                  </Typography>
                  <RunOutcomeChip run={run} />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {agentLabel(run.agent)} · {run.userName ?? 'System'} · {formatWhen(run.startedAt)}
                  {run.activeChildren > 0 ? ` · ${run.activeChildren} subagent${run.activeChildren > 1 ? 's' : ''} working` : ''}
                </Typography>
                {run.plan && (
                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    Plan{run.plan.decision ? ` ${run.plan.decision}` : ''} · {run.plan.stepsDone}/{run.plan.stepsTotal} steps · {run.plan.goal}
                  </Typography>
                )}
              </Box>
            </ListItemButton>
          ))}
        </List>
      )}

      {dataUpdatedAt > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Updated {formatTime(new Date(dataUpdatedAt).toISOString())}
        </Typography>
      )}
    </Paper>
  );
}
